import type { FetchContext, FetchOptions } from 'ofetch';
import { extractFetchErrorStatusCode } from '~/utils/extractFetchErrorStatusCode';
import { StatusCodes } from 'http-status-codes';

export type MagentoFetchOptions = FetchOptions & {
  /** Internal guard — prevents infinite 401 retry loops. */
  _magentoAuthRetried?: boolean;
};

const MAGENTO_API_PATH = '/api/magento/';

function isMagentoApiRequest(request: FetchContext['request']): boolean {
  const url =
    typeof request === 'string'
      ? request
      : request instanceof URL
        ? request.href
        : request.url;

  return url.includes(MAGENTO_API_PATH);
}

/**
 * Handles 401 responses from Magento API routes: one silent refresh attempt,
 * then syncs shop auth status and notifies the user if still unauthenticated.
 * The original 401 error continues to propagate via onResponseError.
 */
export async function handleMagentoFetchAuthError(
  context: FetchContext
): Promise<void> {
  const statusCode = extractFetchErrorStatusCode(context.error);
  const options = context.options as MagentoFetchOptions;

  if (
    statusCode !== StatusCodes.UNAUTHORIZED ||
    !isMagentoApiRequest(context.request) ||
    options._magentoAuthRetried
  ) {
    return;
  }

  options._magentoAuthRetried = true;

  const { silentRefresh, syncStatus, isAuthenticated } = useShopAuth();
  const { notifySessionExpired } = useSessionExpiryNotice();
  const logger = useLogger();

  try {
    await silentRefresh();
    await syncStatus();

    if (!isAuthenticated.value) {
      logger.warn(
        'Magento fetch auth interceptor',
        'Shop session could not be restored after 401'
      );
      notifySessionExpired();
    }
  } catch (error) {
    logger.error(
      'Magento fetch auth interceptor',
      'Failed to reconcile shop auth after 401',
      error
    );
  }
}
